import { useEffect, useMemo, useState } from "react";
import { ArrowRight, BadgeCheck, TicketPercent, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { appApi } from "@/api";
import { auth } from "@/lib/auth";
import { COUPON_PACKAGE_POPUP_EVENT } from "@/lib/session";
import { storage, storageKeys } from "@/lib/storage";
import type { CouponPackagePopup as CouponPackagePopupData } from "@/types/api";

const TEST_COPY_PATTERN = /测试|test|ui test|自动测试/i;

const normalizePopup = (value: CouponPackagePopupData | null) => {
  if (!value?.record_id) {
    return null;
  }
  return value;
};

const cleanRemoteCopy = (value: string | undefined, fallback: string) => {
  const text = value?.trim();
  if (!text || TEST_COPY_PATTERN.test(text)) {
    return fallback;
  }
  return text;
};

export default function CouponPackagePopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [popup, setPopup] = useState<CouponPackagePopupData | null>(null);

  useEffect(() => {
    const stored = normalizePopup(storage.get<CouponPackagePopupData>(storageKeys.couponPackagePopup));
    if (stored) {
      setPopup(stored);
    }

    const handlePopup = (event: Event) => {
      const detail = (event as CustomEvent<CouponPackagePopupData>).detail;
      const nextPopup = normalizePopup(detail);
      if (nextPopup) {
        setPopup(nextPopup);
      }
    };

    window.addEventListener(COUPON_PACKAGE_POPUP_EVENT, handlePopup);
    return () => {
      window.removeEventListener(COUPON_PACKAGE_POPUP_EVENT, handlePopup);
    };
  }, []);

  useEffect(() => {
    if (!auth.getToken()) {
      setPopup(null);
      return;
    }
    const stored = normalizePopup(storage.get<CouponPackagePopupData>(storageKeys.couponPackagePopup));
    if (stored) {
      setPopup(stored);
      return;
    }
    appApi
      .getPendingCouponPackagePopup()
      .then((remotePopup) => {
        const nextPopup = normalizePopup(remotePopup);
        if (nextPopup) {
          storage.set(storageKeys.couponPackagePopup, nextPopup);
          setPopup(nextPopup);
        }
      })
      .catch(() => {
        // Popup is a non-blocking reminder. Coupon ownership has already been granted on the server.
      });
  }, [location.pathname]);

  const copy = useMemo(() => {
    const couponTotal = Number(popup?.coupon_total || 0);
    const displayTotal = couponTotal > 0 ? couponTotal : 1;
    return {
      displayTotal,
      title: cleanRemoteCopy(popup?.title, displayTotal > 1 ? "已为你备好新人礼券" : "送你一张新人礼券"),
      content: cleanRemoteCopy(popup?.content, "首次预约可用，结算时自动抵扣。"),
      ticketLabel: displayTotal > 1 ? "新人礼券包" : "新人专享券",
      ticketHint: displayTotal > 1 ? "多张礼券已放入账户" : "首次下单可直接使用",
    };
  }, [popup]);

  if (!popup || location.pathname.startsWith("/login")) {
    return null;
  }

  const closePopup = async (goCoupons: boolean) => {
    const recordId = popup.record_id;
    setPopup(null);
    storage.remove(storageKeys.couponPackagePopup);
    if (goCoupons) {
      navigate("/coupons");
    }
    try {
      await appApi.markCouponPackagePopupRead(recordId);
    } catch (error) {
      console.warn("[coupon-package-popup] mark read failed", error);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#211711]/55 px-6 backdrop-blur-[2px]">
      <div
        className="relative w-full max-w-[334px] overflow-hidden rounded-[26px] bg-[#FFFCF7] shadow-[0_24px_80px_rgba(32,20,12,0.32)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="coupon-package-popup-title"
      >
        <div className="h-[7px] bg-[linear-gradient(90deg,#C98A52_0%,#F16F2B_48%,#D69B64_100%)]" />
        <button
          type="button"
          aria-label="关闭优惠券弹窗"
          onClick={() => void closePopup(false)}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF8F1] text-[#9C826D] shadow-[0_8px_18px_rgba(70,42,24,0.12)]"
        >
          <X size={18} strokeWidth={2.4} />
        </button>

        <div className="px-6 pb-6 pt-7">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#2E211B] px-3 py-1 text-[11px] font-bold text-[#FFE4C1]">
            <BadgeCheck size={13} strokeWidth={2.4} />
            新客礼遇
          </div>

          <h2 id="coupon-package-popup-title" className="mt-4 text-[25px] font-black leading-tight text-[#221915]">
            {copy.title}
          </h2>
          <p className="mt-2 text-[14px] leading-6 text-[#8B7667]">{copy.content}</p>

          <div className="relative mt-6 overflow-hidden rounded-[20px] border border-[#F2D7C2] bg-white shadow-[0_14px_34px_rgba(173,111,61,0.14)]">
            <div className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full border border-[#F2D7C2] bg-[#FFFCF7]" />
            <div className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full border border-[#F2D7C2] bg-[#FFFCF7]" />
            <div className="flex min-w-0 items-stretch">
              <div className="flex w-[92px] shrink-0 flex-col items-center justify-center bg-[linear-gradient(160deg,#F46F2D_0%,#D78A4E_100%)] px-3 py-5 text-white">
                <TicketPercent size={30} strokeWidth={2.2} />
                <span className="mt-2 text-[11px] font-bold leading-none opacity-90">NEW</span>
              </div>
              <div className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-4">
                <div className="min-w-0">
                  <p className="text-[12px] font-bold text-[#C27B45]">{copy.ticketLabel}</p>
                  <p className="mt-1 text-[16px] font-black leading-snug text-[#241914]">{copy.ticketHint}</p>
                </div>
                <div className="flex shrink-0 items-end text-[#F05F22]">
                  <span className="text-[34px] font-black leading-none">{copy.displayTotal}</span>
                  <span className="mb-1 ml-1 text-[13px] font-black">张</span>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-4 text-center text-[12px] leading-5 text-[#B7A393]">
            优惠券已放入账户，符合条件的订单会自动匹配。
          </p>

          <button
            type="button"
            onClick={() => void closePopup(true)}
            className="mt-5 flex h-12 w-full items-center justify-center gap-1.5 rounded-full bg-[#F16322] text-[15px] font-black text-white shadow-[0_14px_28px_rgba(241,99,34,0.25)]"
          >
            查看可用礼券
            <ArrowRight size={18} strokeWidth={2.6} />
          </button>
          <button
            type="button"
            onClick={() => void closePopup(false)}
            className="mt-3 h-9 w-full text-[13px] font-bold text-[#9C8B7D]"
          >
            先逛逛
          </button>
        </div>
      </div>
    </div>
  );
}
